import { cloneDeep, isEqual } from 'lodash';

function guardNavigation(to, from, next) {
  // The 401 handler redirects to /login: the session is gone, so there is nothing to save.
  if (to.path === '/login') {
    return next();
  }
  if (this.isSaving) {
    return next(false);
  }
  this.confirmDiscard().then((canLeave) => (canLeave ? next() : next(false)));
}

/**
 * Unsaved-changes tracking for the tabs of the user detail page.
 *
 * The host component must implement:
 *   getFormValues()       Returns a plain object of the current editable values.
 *                         Use primitives and plain arrays or objects (no moment
 *                         instances) so values compare by content.
 *   setFormValues(values) Applies such an object to the form.
 *
 * Call markPristine() once the form is first filled and after every successful
 * save. isDirty then tells whether the form differs from that snapshot.
 * Hosts using an Ant `v-decorator` form (whose state is not reactive) must call
 * touchForm() from the form's `onValuesChange`; hosts bound to reactive data do not.
 *
 * Leaving the tab (route change, browser close or reload) while dirty asks for
 * confirmation. Use it on the component that is the route's `component`, otherwise
 * `beforeRouteLeave` never fires. Switching to another user on the same tab
 * (`beforeRouteUpdate`) asks too. Redirects to /login are never blocked.
 */
export default {
  data() {
    return {
      pristine: null,
      isSaving: false,
      formRevision: 0
    };
  },
  computed: {
    isDirty() {
      if (this.pristine === null) {
        return false;
      }
      // Re-evaluate whenever touchForm, markPristine or undo ran. The bare read
      // below exists only to register formRevision as a reactive dependency.
      this.formRevision;
      return !isEqual(this.getFormValues(), this.pristine);
    }
  },
  mounted() {
    window.addEventListener('beforeunload', this.warnBeforeUnload);
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.warnBeforeUnload);
  },
  beforeRouteLeave: guardNavigation,
  beforeRouteUpdate(to, from, next) {
    if (to.params.id === from.params.id) {
      return next();
    }
    guardNavigation.call(this, to, from, next);
  },
  methods: {
    markPristine() {
      this.pristine = cloneDeep(this.getFormValues());
      this.formRevision++;
    },
    touchForm() {
      this.formRevision++;
    },
    undo() {
      this.setFormValues(cloneDeep(this.pristine));
      this.formRevision++;
    },
    confirmDiscard() {
      if (!this.isDirty) {
        return Promise.resolve(true);
      }
      return new Promise((resolve) => {
        this.$confirm({
          title: 'Discard unsaved changes?',
          content: 'You have unsaved changes on this tab. They will be lost if you leave.',
          okText: 'Discard',
          okType: 'danger',
          cancelText: 'Stay',
          onOk: () => resolve(true),
          onCancel: () => resolve(false)
        });
      });
    },
    warnBeforeUnload(event) {
      if (this.isDirty) {
        event.preventDefault();
        event.returnValue = '';
      }
    }
  }
};
